import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, CheckCircle2, PauseCircle, CalendarRange, CalendarDays, Wallet, Receipt, PlusCircle, Loader2, AlertTriangle } from "lucide-react";
import { PageHeader, Panel, StatusBadge, JobTypeChip } from "@/components/common";
import { FlaggedJobs } from "@/components/FlaggedJobs";
import { IntakeStats } from "@/components/IntakeStats";
import { LiveBadge } from "@/components/LiveBadge";
import { OverdueInvoices } from "@/components/OverdueInvoices";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { money, fmtTime, todayISO, fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const Stat = ({ id, label, value, icon: Icon, tone = "navy", to }) => {
  const tones = { navy: "text-[#0E1E38] bg-sky-50", green: "text-emerald-700 bg-emerald-50", amber: "text-amber-700 bg-amber-50", rose: "text-rose-700 bg-rose-50" };
  const body = (
    <div className="group h-full rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-sky-300" data-testid={`stat-card-${id}`}>
      <div className="flex items-start justify-between">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        <span className={cn("grid h-8 w-8 place-items-center rounded-lg", tones[tone])}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-3 font-mono text-3xl font-bold tracking-tight text-slate-900" data-testid={`stat-value-${id}`}>{value}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const Group = ({ title, children, cols = "sm:grid-cols-3" }) => (
  <div>
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</h2>
    <div className={cn("grid grid-cols-1 gap-4", cols)}>{children}</div>
  </div>
);

export default function DashboardPage() {
  const today = todayISO();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "owner"].includes(user?.role);
  const { data: s, updatedAt } = useFetch("/dashboard", { today });
  const { data: jobs } = useFetch("/bookings", { date: today });
  const todays = (jobs || []).filter((j) => !["Cancelled", "Refunded"].includes(j.status));

  if (!s) return <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={fmtDate(today, "EEEE d MMMM yyyy")}><LiveBadge updatedAt={updatedAt} /></PageHeader>
      <Group title="Today" cols="sm:grid-cols-2 lg:grid-cols-4">
        <Stat id="jobs-today" label="Jobs today" value={s.jobs_today} icon={CalendarCheck} to="/calendar" />
        <Stat id="confirmed" label="Confirmed jobs today" value={s.confirmed_today} icon={CheckCircle2} tone="green" />
        <Stat id="on-hold" label="Upcoming jobs on hold" value={s.on_hold} icon={PauseCircle} tone="amber" to="/bookings" />
        <Stat id="flagged" label="Upcoming jobs missing info" value={s.flagged} icon={AlertTriangle} tone="rose" to="/bookings/flagged" />
      </Group>
      <FlaggedJobs total={s.flagged} />
      <IntakeStats today={today} />
      {isAdmin && <OverdueInvoices />}
      <Group title="Upcoming" cols="sm:grid-cols-2">
        <Stat id="next-7-days" label="Jobs in next 7 days" value={s.next_7_days} icon={CalendarRange} to="/bookings" />
        <Stat id="next-30-days" label="Jobs in next 30 days" value={s.next_30_days} icon={CalendarDays} to="/bookings" />
      </Group>
      {isAdmin && <Group title="Money">
        <Stat id="deposits-collected" label="Deposits collected" value={money(s.deposits_collected)} icon={Wallet} tone="green" to="/payments" />
        <Stat id="driver-extras" label="Driver extras charged" value={money(s.driver_extras)} icon={PlusCircle} to="/payments" />
        <Stat id="outstanding-balances" label="Outstanding balances" value={money(s.outstanding_balances)} icon={Receipt} tone="rose" to="/payments" />
      </Group>}
      <Panel title={`Today's jobs (${todays.length})`} action={<Link to="/calendar" className="text-sm font-medium text-sky-700 hover:underline" data-testid="link-open-calendar">Open calendar</Link>}>
        {todays.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="today-empty">No jobs booked for today.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="today-jobs-list">
            {todays.map((j) => (
              <li key={j.id} onClick={() => navigate(`/bookings/${j.id}`)} className="flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 py-3 hover:bg-slate-50" data-testid={`today-job-${j.id}`}>
                <span className="w-16 font-mono text-sm font-semibold">{fmtTime(j.start_time)}</span>
                <span className="font-mono text-xs font-bold text-sky-700">{j.job_number}</span>
                <span className="min-w-[140px] font-medium">{j.customer_name}</span>
                <JobTypeChip name={j.job_type} />
                <span className="flex-1 truncate text-sm text-slate-500">{j.pickup_address} → {j.delivery_address}</span>
                <span className="text-sm text-slate-600">{j.assigned_team || "Unassigned"}</span>
                <StatusBadge status={j.status} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
